import React, { useEffect, useState } from 'react';
import { ListProps, TextField } from 'react-admin';

import {
  TextInput,
  Grid,
  GridList,
  Forbidden,
  CheckboxField,
  MoneyField,
  SelectInput,
  DateField,
  DateRangeSelect,
  NullableBooleanInput,
  BooleanField,
  MoneyUsdField,
} from '@components';
import { useExporter, useRbacList } from '@hooks';
import { FinanceDeposit, FinanceDepositOptions, purposeOptions } from '@types';
import { financeDepositHttp } from '@network';
import { FinanceDocsField } from '@pages/finance-withdrawal/FinanceDocsField';
import { useListContext } from 'ra-core';
import { moneyFormatter } from '@utils';
import cls from './finance-deposit-list.module.css';
import { useSettingsContext } from '@providers';

const clientFilterOptions = [
  { id: 'true', name: 'Yes' },
  { id: 'false', name: 'No' },
];

const receivedFilterOptions = [
  { id: 'true', name: 'Yes' },
  { id: 'false', name: 'No' },
];

const TotalAmount = () => {
  const { data, selectedIds } = useListContext<FinanceDeposit>();
  if (!data || !selectedIds.length) return null;
  const list: FinanceDeposit[] =
    selectedIds.length > 0
      ? (data || []).filter((i) => selectedIds.includes(i.id))
      : (data || []);
  const total = list.reduce((acc, r) => acc + (r.amount ?? 0), 0);

  return <div className={cls.total}>Total Amount: {moneyFormatter.format(total)}</div>;
};

export const FinanceDepositList = (props: ListProps) => {
  const { filterValues } = useSettingsContext();
  const rbacList = useRbacList();
  const isPesoAmount = filterValues['isMixed'] === false;

  const exporter = useExporter({
    filename: 'Deposits',
    props: {
      'id': {
        label: 'ID',
      },
      'code': {
        label: 'Concept',
      },
      'accountAccount': {
        label: 'Account',
      },
      'creditCode': {
        label: 'Credit Code',
      },
      'amount': {
        label: 'Amount',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'amountUsd',
      },
      'paymentDate': {
        label: 'Payment Date',
        transform: 'date',
      },
      'creditAccredited': {
        label: 'Accredited',
      },
      'purpose': {
        label: 'Purpose',
      },
      'details': {
        label: 'Details',
      },
      'received': {
        label: 'Received',
        transform: 'boolean',
      },
      'receipts': {
        label: 'Receipts',
        transform: 'files',
      },
      'invoices': {
        label: 'Invoices',
        transform: 'files',
      },
      'isUsd': {
        label: 'Currency',
        transform: 'currency',
      },
      'exchangeRate': {
        label: 'Exch. Rate',
        transform: 'money',
      },
    },
    extraRequest: async (records: any[]) => {
      if (!records || !Array.isArray(records) || records.length === 0) {
        return Promise.resolve();
      }

      const ids = records
        .map(record => record?.id)
        .filter(id => id != null && typeof id === 'number');

      if (ids.length > 0) {
        return await financeDepositHttp.downloadBulkFiles(ids);
      }

      return Promise.resolve();
    },
  });

  const [depositConceptsOptions, setDepositConceptsOptions] =
    useState<{id: string; name: string;}[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const depositOptions: FinanceDepositOptions = await financeDepositHttp.options();

        if (!depositOptions) {
          throw new Error('The lists of options has not been received');
        }

        setDepositConceptsOptions((depositOptions?.concept || []).map(option => ({
          id: option,
          name: option,
        })));
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList {...props}
      exporter={exporter}
      filters={[
        <TextInput source="q" key="q" label="Search" alwaysOn style={{ width: 220 }} />,
        <SelectInput key="purpose" source="purpose" choices={purposeOptions} label="Purpose" />,
        <SelectInput key="concept"
          source="concept"
          choices={depositConceptsOptions}
          label="Concept" />,
        <SelectInput key="creditCode"
          source="creditCode"
          choices={clientFilterOptions}
          label="Client" />,
        <SelectInput key="received"
          source="received"
          choices={receivedFilterOptions}
          label="Received" />,
        <DateRangeSelect key="createdAt"
          source="createdAt"
          label="Created/Payment date"
          alwaysOn
          choices={[
            { id: 'createdAt', name: 'Created At' },
            { id: 'paymentDate', name: 'Payment Date' },
          ]}
        />,
        <NullableBooleanInput key="isUsd"
          source="isUsd"
          label="Currency"
          trueLabel="USD"
          falseLabel="MXN"
        />,
        <NullableBooleanInput key="isMixed"
          source="isMixed"
          label="Currency Selection"
          nullLabel=""
          defaultValue={true}
          trueLabel="Mixed"
          falseLabel="MXN"
        />,
      ]}
      sort={{ field: 'id', order: 'DESC' }}
      className={cls.page}
      summary={<TotalAmount />}
    >
      <Grid
        rowClick="edit"
        className={cls.grid}
        defaultColumns={'code amount paymentDate purpose received receipts'.split(' ')}
      >
        <TextField source="id" label="ID" />
        <TextField source="code" label="Concept" />
        <TextField source="accountAccount" label="Account" />
        <TextField source="creditCode" label="Credit code" />
        <MoneyUsdField source="amount" label="Amount" sourceUsd="amountUsd" />
        <BooleanField source="isUsd" label="Currency" valueLabelTrue="USD" valueLabelFalse="MXN" />
        <MoneyField source="exchangeRate" label="Exch. Rate" />
        <DateField source="paymentDate" label="Payment Date" />
        <TextField source="creditAccredited" label="Accredited" />
        <TextField source="purpose" label="Purpose" />
        <TextField source="details" label="Details" />
        <CheckboxField source="received" label="Received" />
        <FinanceDocsField source="receipts" label="Receipts" modelName="Deposit" />
        <FinanceDocsField source="invoices" label="Invoices" modelName="Deposit" />
        <DateField source="createdAt" label="Created at" />
      </Grid>
    </GridList>
  );
};
